import { logger } from "../../../lib/Logger";

export type MermaidSurface =
  | "chat"
  | "note-preview"
  | "jan-export"
  | "download"
  | "public-export";

export type MermaidFailureKind =
  | "parse-error"
  | "render-exception"
  | "empty-svg"
  | "error-diagram"
  | "bare-br"
  | "invalid-svg"
  | "image-load"
  | "upload"
  | "missing-diagram"
  | "capture";

const KIND_MESSAGE: Record<MermaidFailureKind, string> = {
  "parse-error": "Mermaid diagram failed to parse",
  "render-exception": "Mermaid diagram failed to render",
  "empty-svg": "Mermaid returned an empty SVG",
  "error-diagram": "Mermaid rendered an error diagram",
  "bare-br":
    "Mermaid SVG contains a bare line break and will not display as an image",
  "invalid-svg":
    "Mermaid SVG is not well-formed and will not display as an image",
  "image-load": "Mermaid diagram image failed to load",
  upload: "Mermaid diagram upload failed",
  "missing-diagram": "Rendered Mermaid diagram was missing",
  capture: "Mermaid diagram capture failed",
};

/** A `<br>` that is neither self-closing nor paired with `</br>`. Those break SVG images. */
function hasBareBr(markup: string): boolean {
  const withoutClosed = markup
    .replace(/<br\s*\/>/gi, "")
    .replace(/<br\s*>\s*<\/br>/gi, "");
  return /<br\s*>/i.test(withoutClosed);
}

/** Turn a standalone `<br>` into `<br/>`. Leave `<br></br>` pairs unchanged. */
export function closeBareBrTags(markup: string): string {
  return markup.replace(/<br>/gi, (match, offset: number, source: string) => {
    const rest = source.slice(offset + match.length);
    if (/^\s*<\/br>/i.test(rest)) return match;
    return "<br/>";
  });
}
const ERROR_SVG_RE = /class=["'][^"']*\berror-(?:text|icon)\b/;

export function reportMermaidFailure(
  surface: MermaidSurface,
  kind: MermaidFailureKind,
  extras?: Record<string, string | number | boolean | null | undefined>
): void {
  const error = new Error(KIND_MESSAGE[kind]);
  error.name = "MermaidDisplayError";
  logger.captureException(error, {
    tags: {
      feature: "mermaid",
      mermaid_surface: surface,
      mermaid_failure: kind,
    },
    extras,
    fingerprint: ["mermaid", surface, kind],
  });
}

/** Reports a thrown render or upload error without sending the diagram text. */
export function reportMermaidThrown(
  surface: MermaidSurface,
  error: unknown,
  extras?: Record<string, string | number | boolean | null | undefined>
): void {
  const message = error instanceof Error ? error.message : "";
  const kind: MermaidFailureKind = /parse error/i.test(message)
    ? "parse-error"
    : /upload|presigned/i.test(message)
      ? "upload"
      : "render-exception";
  reportMermaidFailure(surface, kind, {
    ...extras,
    errorName: error instanceof Error ? error.name : typeof error,
  });
}

export function svgMarkupFromOutput(value: string): string | null {
  const encoded = value.match(
    /^data:image\/svg\+xml(?:;charset=[^;,]+)?;base64,(.+)$/i
  );
  if (encoded) {
    try {
      const binary = atob(encoded[1]);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch {
      return null;
    }
  }

  const utf8 = value.match(/^data:image\/svg\+xml(?:;charset=[^;,]+)?,(.+)$/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return null;
    }
  }

  if (value.includes("<svg")) return value;
  return null;
}

/**
 * Problems that mean the diagram failed to generate, or will not display
 * where the SVG is loaded as an image. Chat inserts the SVG as HTML, so a
 * bare `<br>` is not a failure there.
 */
export function mermaidOutputProblem(
  value: string,
  asImage: boolean
): MermaidFailureKind | null {
  if (!value.trim()) return "empty-svg";

  const markup = svgMarkupFromOutput(value);
  if (!markup) {
    return asImage && /^data:image\/svg/i.test(value) ? "invalid-svg" : null;
  }
  if (!markup.trim()) return "empty-svg";
  if (ERROR_SVG_RE.test(markup)) return "error-diagram";
  if (!asImage) return null;
  if (hasBareBr(markup)) return "bare-br";

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(markup, "image/svg+xml");
    if (doc.querySelector("parsererror")) return "invalid-svg";
  }

  return null;
}

export function reportInvalidMermaidOutput(
  surface: MermaidSurface,
  value: string,
  asImage: boolean
): void {
  const kind = mermaidOutputProblem(value, asImage);
  if (!kind) return;
  reportMermaidFailure(surface, kind, { svgLength: value.length });
}
